/**
 * The PN consultation card v2.0 (BAR-2994): one card for every track.
 *
 * The shipped card (`pn-consultation-script.ts`) has a section per payment
 * path, each graying itself out under its own rules. v2.0 has six sections for
 * every patient, and the track the navigator picks on the card decides which
 * payment path the Money check and the Next step show. Spec:
 * everself-design-system `15-insurance-project/pn-consultation-card.mdx`; copy
 * for rows the shipped card did not have comes from its prototype
 * (`mockups/pn-consultation-v2.html`). Rows the shipped card already had keep
 * their shipped copy.
 *
 * SAME NOTE, SAME KEYS. Every answer keeps the id the shipped card writes, so a
 * note drafted on either card reads on the other, and the answers of a track
 * the card is not showing stay on the draft. A track's rows sit in groups gated
 * on the track the script was built for, so `answersThatApply` leaves another
 * track's answers out of the summary and the actions without deleting them.
 *
 * The track chips only change what the card shows. Switching the patient's
 * track happens when the card is saved (BAR-2995), so the chip is recorded
 * under its own id, {@link PN_CARD_TRACK_ID}.
 */

import { BenefitAdministrator, Track, TrackLabel } from './constants';
import {
  ADMINISTRATOR_LABELS,
  CARE,
  describeEmployerBenefit,
  employerSectionDisabledReason,
  INSURANCE_CHECKING_STATE,
  insuranceTravelMarket,
  hasBenefitsThrough,
  hasEmployerBenefitResult,
  hasInsuranceSource,
  hasNoBenefit,
  hasPartnerBenefit,
  hasPrivateSource,
  INSURANCE_TRAVEL_OPTIONS,
  hasWhosePlan,
  isViaFamily,
  meetsInsuranceCriteria,
  missesInsuranceCriteria,
  PN_PROCEDURE_TIMING_OPTIONS,
  PN_BARRIER_OPTIONS,
  PN_BENEFIT_VERIFICATION_OUTCOME,
  PN_BOOK_ONCE_APPROVED_ID,
  PN_BOOK_ONCE_APPROVED_OPTIONS,
  PN_COMMITTED_OUTCOME,
  PN_EMPLOYER_BENEFIT_ID,
  PN_EMPLOYER_ID,
  PN_EMPLOYER_TASK_ID,
  PN_BENEFITS_THROUGH_ID,
  PN_BENEFITS_THROUGH_OPTIONS,
  PN_EMPLOYMENT_STATUS_ID,
  PN_EMPLOYMENT_STATUS_OPTIONS,
  PN_FAMILY_MEMBER_NAME_ID,
  PN_GLP1_OPTIONS,
  PN_INSURANCE_BOOK_ONCE_APPROVED_ID,
  PN_INSURANCE_ELIGIBILITY_ID,
  PN_INSURANCE_SOURCE_ID,
  PN_INSURANCE_TASK_ID,
  PN_INSURANCE_TRAVEL_ID,
  PN_INSURER_ID,
  PN_OUTCOME_OPTIONS,
  PN_POLICYHOLDER_ID,
  PN_POLICYHOLDER_OPTIONS,
  PN_SMOKING_OPTIONS,
  PN_SOURCE_OPTIONS,
  PnOutcome,
  PREGNANCY_QUESTION,
} from './pn-consultation-script';
import {
  CARECREDIT_URL,
  EXPLAIN_DUR,
  EXPLAIN_PROC,
  PacBeat,
  PacBeatAnswers,
  PacWidget,
  PROCEDURE_OPTIONS,
} from './patient-access-script';
import { INSURANCE_SOURCE_OPTIONS } from './insurance-eligibility';
import {
  WEIGHT_RELATED_CONDITIONS,
  WEIGHT_RELATED_CONDITIONS_ID,
  WeightRelatedCondition,
} from './weight-related-conditions';

/* -------------------------------------------------------------------------
   The track the card shows
   ------------------------------------------------------------------------- */

export const PN_CARD_TRACKS = ['self-pay', 'employer', 'insurance'] as const;
export type PnCardTrack = (typeof PN_CARD_TRACKS)[number];

export const PN_CARD_TRACK_LABELS: Record<PnCardTrack, string> = {
  'self-pay': 'Self-pay',
  employer: 'Employer',
  insurance: 'Insurance',
};

/**
 * The track chip the navigator picked. Recorded only when a chip is clicked:
 * until then the card shows the patient's own track. Not the patient's track:
 * that switches when the card is saved (BAR-2995).
 */
export const PN_CARD_TRACK_ID = 'card_track';

/**
 * Marks a note worked on the v2.0 card, written with the navigator's first
 * answer. A marked note opens on v2.0 for everyone, so a user without the flag
 * never finishes a v2.0 draft under the shipped card's rules.
 */
export const PN_CARD_VERSION_ID = 'card_version';
export const PN_CARD_V2 = 'v2';

export function isPnCardV2Note(answers: PacBeatAnswers): boolean {
  return answers.values[PN_CARD_VERSION_ID] === PN_CARD_V2;
}

export function isPnCardTrack(value: unknown): value is PnCardTrack {
  return PN_CARD_TRACKS.includes(value as PnCardTrack);
}

/**
 * The chip a patient's track starts on. No track reads as self-pay, as the
 * backend reads it; an unverified employer is still the employer path.
 */
export function cardTrackForPatientTrack(track: Track | undefined): PnCardTrack {
  switch (track) {
    case Track.CARRUM:
    case Track.TRANSCARENT:
    case Track.EMPLOYER:
      return 'employer';
    case Track.INSURANCE:
      return 'insurance';
    default:
      return 'self-pay';
  }
}

/**
 * The patient's track the chip asks for on save (BAR-2995): self-pay and
 * insurance as they are; the employer path only once a partner benefit names
 * the administrator, since a partner track is per administrator. Undefined
 * when there is no track to switch to yet.
 */
export function switchTargetTrack(
  track: PnCardTrack,
  administrator: BenefitAdministrator | undefined
): Track | undefined {
  if (track === 'self-pay') return Track.D2C;
  if (track === 'insurance') return Track.INSURANCE;
  if (administrator === BenefitAdministrator.CARRUM) return Track.CARRUM;
  if (administrator === BenefitAdministrator.TRANSCARENT) return Track.TRANSCARENT;
  return undefined;
}

/* -------------------------------------------------------------------------
   Outcome by track (R10)
   ------------------------------------------------------------------------- */

/** Self-pay never offers "benefit verification next": there is no benefit to verify. */
export const PN_SELF_PAY_OUTCOME_OPTIONS: readonly PnOutcome[] = PN_OUTCOME_OPTIONS.filter(
  (outcome) => outcome !== PN_BENEFIT_VERIFICATION_OUTCOME
);

/** The coverage tracks never offer "likely payment in 72 hours": approval comes first. */
export const PN_COVERAGE_OUTCOME_OPTIONS: readonly PnOutcome[] = PN_OUTCOME_OPTIONS.filter(
  (outcome) => outcome !== PN_COMMITTED_OUTCOME
);

export function outcomeOptionsForCardTrack(track: PnCardTrack): readonly PnOutcome[] {
  return track === 'self-pay' ? PN_SELF_PAY_OUTCOME_OPTIONS : PN_COVERAGE_OUTCOME_OPTIONS;
}

/** The outcome answer's id, the same on both cards. */
const OUTCOME_ID = 'close';

/**
 * The answers with the track chip moved to `track`. A chosen outcome the new
 * track doesn't offer is cleared; every other answer stays, so switching back
 * finds them where they were. The same object when nothing changes.
 */
export function withCardTrack<T extends PacBeatAnswers>(answers: T, track: PnCardTrack): T {
  const outcome = answers.values[OUTCOME_ID];
  const clearOutcome =
    typeof outcome === 'string' && !(outcomeOptionsForCardTrack(track) as readonly string[]).includes(outcome);
  if (answers.values[PN_CARD_TRACK_ID] === track && !clearOutcome) return answers;
  return {
    ...answers,
    values: {
      ...answers.values,
      [PN_CARD_TRACK_ID]: track,
      ...(clearOutcome ? { [OUTCOME_ID]: undefined } : {}),
    },
  };
}

/* -------------------------------------------------------------------------
   Condition names
   ------------------------------------------------------------------------- */

/**
 * What the card calls each weight-related condition. The note keeps the
 * clinical name, the one the insurers' criteria list, so nothing changes for
 * coding or the eligibility line.
 */
export const WEIGHT_RELATED_CONDITION_NAMES: Readonly<Record<WeightRelatedCondition, string>> = {
  'Type 2 diabetes': 'Type 2 diabetes',
  'Poorly controlled hypertension': 'Hypertension',
  Hyperlipidemia: 'High cholesterol',
  'Coronary artery or cardiovascular disease': 'Cardiovascular disease',
  'Obstructive sleep apnea': 'Sleep apnea',
  'Pulmonary hypertension': 'Pulmonary hypertension',
  'MASLD/MASH or other obesity-related fatty liver disease': 'Fatty liver disease',
  'Weight-bearing joint disease': 'Joint disease',
  'Lower-extremity lymphatic or venous obstruction': 'Leg swelling',
  'Medically refractory GERD': 'Acid reflux',
};

/* -------------------------------------------------------------------------
   Sections
   ------------------------------------------------------------------------- */

export const PN_V2_SECTION_IDS = ['intro', 'check', 'procedure', 'medical', 'next', 'outcome'] as const;
export type PnV2SectionId = (typeof PN_V2_SECTION_IDS)[number];

const FRAMING: Record<PnCardTrack, string> = {
  'self-pay':
    'This call will be about 25 minutes. We’ll talk about what you’re hoping for, check that the procedure is a good fit medically, and go over ways to pay. Then we’ll plan next steps together.',
  employer:
    'This call will be about 25 minutes. We’ll talk about what you’re hoping for, confirm your employer benefit, check that the procedure is a good fit medically, and plan next steps together.',
  insurance:
    'This call will be about 25 minutes. We’ll talk about what you’re hoping for, check your insurance coverage, make sure the procedure is a good fit medically, and go over how insurance approval works. Then we’ll plan next steps together.',
};

/** One row of script, three questions to ask in turn. Nothing to record. */
const WHY_NOW = [
  'What made you reach out now? What have you tried so far?',
  'What does losing weight look like for you? Do you have a goal weight in mind?',
  'Picture yourself six months from now. What would feel different?',
].join('\n\n');

/** The shipped card's two procedure lines, as one: what it is, then the day itself. */
const HOW_IT_WORKS: Record<string, string> = Object.fromEntries(
  PROCEDURE_OPTIONS.map((procedure) => [procedure, `${EXPLAIN_PROC[procedure]} ${EXPLAIN_DUR[procedure]}`])
);

const EMPLOYER_APPROVAL =
  'Employers usually cover 95–100% of the cost. You may need to fly to another Everself location; your employer usually covers travel and accommodations. We’ll check with your employer, which usually takes 2 business days, and we’ll be in touch once it’s confirmed.';

const INSURANCE_APPROVAL =
  'We’ll submit a prior authorization to your insurance. It usually takes 1–2 months to hear back. A care coordinator will set up 2–3 virtual visits with our dietitian and nurse practitioner to build your case. Or you can self-pay with financing and have the procedure in 2–3 weeks.';

const BOOK_ONCE_APPROVED_SAY = 'Assuming your case is approved, would you like us to book your procedure?';

/* -------------------------------------------------------------------------
   Next step (BAR-3000)
   ------------------------------------------------------------------------- */

/**
 * Self-pay payment, several at once: most patients combine a few. Its own id,
 * a list: the shipped card's single `pay` answer is a string other readers
 * expect.
 */
export const PN_PAYMENT_TYPE_OPTIONS = [
  'Cherry',
  'PatientFi',
  'CareCredit',
  'Credit card or bank transfer',
  'HSA or FSA',
] as const;
export const PN_PAYMENT_TYPES_ID = 'pay_types';
const [CHERRY, PATIENTFI, CARECREDIT] = PN_PAYMENT_TYPE_OPTIONS;

/** The employer path's Location, a market name from `ELIGIBLE_LOCATIONS`. */
export const PN_EMPLOYER_LOCATION_ID = 'emp_location';

/** When the Cherry application link was last texted from the card: a record of something done, not an answer. */
export const PN_CHERRY_APPLICATION_SENT_ID = 'cherry_app_sent';

/** The payment types picked, in list order. */
export function readPaymentTypes(values: Record<string, unknown>): string[] {
  const picked = values[PN_PAYMENT_TYPES_ID];
  return Array.isArray(picked) ? PN_PAYMENT_TYPE_OPTIONS.filter((option) => picked.includes(option)) : [];
}

const paysWith =
  (option: string) =>
  (answers: PacBeatAnswers): boolean =>
    readPaymentTypes(answers.values).includes(option);

const SELF_PAY_NEXT_STEP: PacWidget[] = [
  {
    kind: 'locationSelect',
    id: 'loc',
    label: 'Location',
    say: 'Your nearest location would be [location], with Dr. [provider name].',
    eligibleOnly: true,
  },
  { kind: 'computed', field: 'sitePhysicians', label: 'Physicians at this location' },
  {
    kind: 'action',
    label: 'Confirm the date',
    say: 'It looks like we have a couple of slots left on [date]. Are you ready to move forward now? Just so you know, we can’t confirm the date until your payment is in — but I can pencil you in for it today.',
  },
  { kind: 'procedureDateGrid', id: 'procedure_date' },
  {
    kind: 'chips',
    id: 'procdate',
    label: 'If not one of those',
    options: PN_PROCEDURE_TIMING_OPTIONS,
    say: 'When would work better for you?',
  },
  {
    kind: 'multiChips',
    id: PN_PAYMENT_TYPES_ID,
    label: 'Payment',
    options: PN_PAYMENT_TYPE_OPTIONS,
    say: 'Most patients combine a few ways to pay: financing, HSA or FSA, and a card or bank transfer for the rest. Which would you like to use?',
  },
  // Opens the timeline's payment-order drawer (same action as the warm hand-off).
  { kind: 'actionLink', label: 'Payment order', linkText: 'Create payment order', action: 'openPaymentOrder' },
  {
    kind: 'group',
    when: paysWith(CHERRY),
    items: [
      {
        kind: 'computed',
        field: 'cherryApplication',
        label: 'Cherry application',
        say: 'Let’s get your financing done together now, while we’re on the call, so I can answer any questions as you go. I’m texting you the Cherry application; it takes about 5 minutes.',
      },
    ],
  },
  {
    kind: 'group',
    when: paysWith(PATIENTFI),
    items: [
      {
        kind: 'actionLink',
        label: 'PatientFi',
        linkText: 'Open PatientFi',
        action: 'openPatientFi',
        say: 'Let’s fill in the PatientFi application together while we’re on the phone.',
      },
    ],
  },
  {
    kind: 'group',
    when: paysWith(CARECREDIT),
    items: [
      {
        kind: 'actionLink',
        label: 'CareCredit',
        linkText: CARECREDIT_URL.replace(/^https:\/\//, ''),
        action: 'openCareCredit',
        say: 'Let’s do the CareCredit application together now, so I can help with any questions.',
      },
    ],
  },
  {
    kind: 'action',
    label: 'Close',
    say: 'So the next step is payment. Once that’s in, your date is confirmed and our care team takes it from there.',
  },
];

/** Rows shown only while the card is on `track`. Their answers stay on the draft otherwise. */
function onTrack(shown: PnCardTrack, track: PnCardTrack, items: PacWidget[]): PacWidget {
  return { kind: 'group', when: () => shown === track, items };
}

const employerApplies = (answers: PacBeatAnswers): boolean => !employerSectionDisabledReason(answers);
/** Section 3 confirms the procedure; until then, a coverage path is waiting on it, not ruled out. */
const hasProcedure = (answers: PacBeatAnswers): boolean => typeof answers.values.procedure === 'string';
/** Why a coverage path doesn't apply yet, when the reason is only that the procedure isn't confirmed. */
export const CONFIRM_PROCEDURE_FIRST = 'Confirm the procedure first';

export interface PnConsultationV2ScriptContext {
  /** The track the card shows. */
  track: PnCardTrack;
  /** From `pregnancyQuestionApplies`. */
  askPregnancy: boolean;
  /**
   * Why insurance doesn't apply to this patient (`insuranceSectionDisabledReason`),
   * or undefined when it does. Shown in place of the insurance rows; their
   * answers stay on the draft.
   */
  insuranceUnavailable?: string;
  /** Whether the identity rows show. Only while the note is being written, as on the shipped card. */
  confirmDetails?: boolean;
  /** Whether the member ID and group number rows show. Only while the note is being written. */
  insuranceCard?: boolean;
}

/**
 * The v2.0 card's six sections for one patient on one track:
 *   Intro and framing → Money check → Procedure and why Everself →
 *   Medical clearance → Next step → Outcome
 *
 * Only the framing line, the Money check, the Next step and the Outcome list
 * change with the track. Every section has every track's rows, gated on the
 * track, so the answers of the others leave the summary but stay on the note.
 */
export function buildPnConsultationV2Script({
  track,
  askPregnancy,
  insuranceUnavailable,
  confirmDetails = true,
  insuranceCard = true,
}: PnConsultationV2ScriptContext): readonly PacBeat[] {
  /** Insurance waiting on the procedure or the state read is still on offer, not ruled out. */
  const insuranceWaiting =
    insuranceUnavailable === CONFIRM_PROCEDURE_FIRST || insuranceUnavailable === INSURANCE_CHECKING_STATE;
  const intro: PacBeat = {
    id: 'intro',
    title: 'Intro and framing',
    items: [
      {
        kind: 'action',
        label: 'Introduce yourself',
        say: 'Hi [first name], I’m [rep] — I’m a patient navigator here at Everself.',
      },
      {
        kind: 'chips',
        id: 'source',
        label: 'How did they hear about us?',
        options: PN_SOURCE_OPTIONS,
        say: 'How did you hear about Everself?',
      },
      { kind: 'action', label: 'Why now?', say: WHY_NOW },
      {
        kind: 'chips',
        id: 'mo_glp',
        label: 'GLP-1 experience',
        options: PN_GLP1_OPTIONS,
        say: 'Have you tried a GLP-1 like Ozempic or Wegovy?',
      },
      { kind: 'action', label: 'Frame the call', say: FRAMING[track] },
    ],
  };

  const identity: PacWidget[] = confirmDetails
    ? [
        {
          kind: 'profileField',
          field: 'legalName',
          label: 'Legal name',
          say: 'Can you confirm your full legal name, as it appears on your ID or insurance card?',
        },
        {
          kind: 'profileField',
          field: 'preferredName',
          label: 'Preferred name (optional)',
          say: 'Is there a name you prefer we call you?',
        },
        { kind: 'profileField', field: 'birthDate', label: 'Date of birth', say: 'And your date of birth?' },
        // Every track (decided 2026-10-19): Cherry needs it on self-pay, and it's the chart's address on any.
        {
          kind: 'profileField',
          field: 'homeAddress',
          label: 'Home address',
          say: 'And your home address? Street, apartment if there is one, city, state and ZIP.',
        },
      ]
    : [];

  const employerCheck: PacWidget[] = [
    {
      kind: 'group',
      when: (answers) => !hasProcedure(answers),
      items: [{ kind: 'note', text: `${CONFIRM_PROCEDURE_FIRST}.` }],
    },
    {
      kind: 'group',
      when: (answers) => hasProcedure(answers) && !employerApplies(answers),
      items: [{ kind: 'note', text: 'Employer benefits cover ESG only.' }],
    },
    {
      kind: 'group',
      when: employerApplies,
      items: [
        // The spec's order: the employer first, then whose job it is (BAR-2997).
        {
          kind: 'employerSearch',
          id: PN_EMPLOYER_ID,
          resultId: PN_EMPLOYER_BENEFIT_ID,
          label: 'Employer',
          say: 'Which company do you get your health benefits through?',
        },
        {
          kind: 'chips',
          id: PN_BENEFITS_THROUGH_ID,
          label: 'Benefits through',
          options: PN_BENEFITS_THROUGH_OPTIONS,
          say: 'Is that your own job, or a family member’s?',
        },
        {
          kind: 'group',
          when: hasBenefitsThrough,
          items: [
            {
              kind: 'chips',
              id: PN_EMPLOYMENT_STATUS_ID,
              label: 'Employment',
              options: PN_EMPLOYMENT_STATUS_OPTIONS,
              say: 'Is that job full-time, or part-time with health benefits?',
            },
          ],
        },
        {
          kind: 'group',
          when: isViaFamily,
          items: [
            {
              kind: 'text',
              id: PN_FAMILY_MEMBER_NAME_ID,
              label: 'Family member’s legal name',
              say: 'What’s their full legal name?',
            },
          ],
        },
        {
          kind: 'group',
          when: hasEmployerBenefitResult,
          items: [{ kind: 'computed', field: 'employerBenefit', label: 'Result' }],
        },
        {
          kind: 'group',
          when: hasNoBenefit,
          items: [
            { kind: 'note', text: 'Insurance or self-pay is the route from here.' },
            // Insurance where it's offered; self-pay where it isn't.
            // Insurance waiting only on the procedure is still on offer.
            {
              kind: 'switchTrackLink',
              to: insuranceUnavailable && !insuranceWaiting ? 'self-pay' : 'insurance',
            },
          ],
        },
      ],
    },
  ];

  // Built in either way, so a patient insurance stops applying to keeps the
  // answers on the draft but out of the summary, as on the shipped card.
  const unavailable: PacWidget[] = insuranceUnavailable
    ? [
        { kind: 'note', text: `Insurance · ${insuranceUnavailable}` },
        // Not before the procedure is confirmed or the state is read: that's waiting, not ruled out.
        ...(insuranceWaiting ? [] : ([{ kind: 'switchTrackLink', to: 'self-pay' }] satisfies PacWidget[])),
      ]
    : [];
  const whenAvailable = (items: PacWidget[]): PacWidget => ({
    kind: 'group',
    when: () => !insuranceUnavailable,
    items,
  });

  const insuranceCheck: PacWidget[] = [
    ...unavailable,
    whenAvailable([
      {
        kind: 'chips',
        id: PN_INSURANCE_SOURCE_ID,
        label: 'How they get it',
        options: INSURANCE_SOURCE_OPTIONS,
        say: 'How do you get your health insurance?',
      },
      {
        kind: 'group',
        when: hasPrivateSource,
        items: [
          {
            kind: 'insurerSelect',
            id: PN_INSURER_ID,
            label: 'Insurer',
            say: 'Who is your health insurance with?',
            notListedOption: true,
          },
          {
            kind: 'chips',
            id: PN_POLICYHOLDER_ID,
            label: 'Whose plan',
            options: PN_POLICYHOLDER_OPTIONS,
            say: 'Is the plan yours, or a spouse’s or parent’s?',
          },
          ...(insuranceCard
            ? ([
                {
                  kind: 'group',
                  // Their own plan too: a policyholder still on the Coverage is offered for clearing.
                  when: hasWhosePlan,
                  items: [
                    {
                      kind: 'policyholderField',
                      label: 'Policyholder legal name and date of birth',
                      say: 'What’s their full legal name, as it is on the card? And their date of birth?',
                    },
                  ],
                },
                {
                  kind: 'insuranceCardField',
                  field: 'memberId',
                  label: 'Member ID',
                  say: 'Do you have your insurance card handy? Could you read me the member ID?',
                },
                {
                  kind: 'insuranceCardField',
                  field: 'groupNumber',
                  label: 'Group number (optional)',
                  say: 'And the group number, if there’s one?',
                },
              ] satisfies PacWidget[])
            : []),
        ],
      },
      {
        kind: 'group',
        when: hasInsuranceSource,
        items: [{ kind: 'insuranceEligibility', id: PN_INSURANCE_ELIGIBILITY_ID, label: 'Likely eligibility' }],
      },
      {
        kind: 'group',
        when: missesInsuranceCriteria,
        items: [
          { kind: 'script', text: 'Self-pay with financing is the way to go for now, and I can walk you through it.' },
          { kind: 'switchTrackLink', to: 'self-pay' },
        ],
      },
    ]),
  ];

  const check: PacBeat = {
    id: 'check',
    title: { 'self-pay': 'Money check', employer: 'Employer benefit', insurance: 'Insurance check' }[track],
    items: [...identity, onTrack('employer', track, employerCheck), onTrack('insurance', track, insuranceCheck)],
  };

  const procedure: PacBeat = {
    id: 'procedure',
    title: 'Procedure and why Everself',
    items: [
      {
        kind: 'select',
        id: 'procedure',
        label: 'Confirm the procedure',
        options: PROCEDURE_OPTIONS,
        say: 'I see you’re interested in [procedure]. Is that right?',
      },
      {
        kind: 'action',
        label: 'How it works',
        sayByProcedure: HOW_IT_WORKS,
        sayFallback: 'Confirm the procedure first — the explanation names it out loud.',
      },
      { kind: 'computed', field: 'weightLossEstimate', label: 'What to expect' },
      { kind: 'action', label: 'Why Everself', say: CARE },
    ],
  };

  const medical: PacBeat = {
    id: 'medical',
    title: 'Medical clearance',
    items: [
      {
        kind: 'computed',
        field: 'bmi',
        label: 'Weight and height',
        say: 'Your weight is [weight] and your height is [height]. Based on that, your BMI is [BMI].',
      },
      {
        kind: 'multiChips',
        id: WEIGHT_RELATED_CONDITIONS_ID,
        label: 'Weight-related conditions',
        options: WEIGHT_RELATED_CONDITIONS,
        optionLabels: WEIGHT_RELATED_CONDITION_NAMES,
        say: 'Do you have any of these? Diabetes, high blood pressure, high cholesterol, sleep apnea, fatty liver, joint pain, or reflux that medication hasn’t fixed?',
      },
      ...(confirmDetails
        ? ([
            {
              kind: 'profileField',
              field: 'birthSex',
              label: 'Sex assigned at birth',
              say: 'For your medical record, what sex were you assigned at birth?',
            },
          ] satisfies PacWidget[])
        : []),
      ...(askPregnancy ? [PREGNANCY_QUESTION] : []),
      {
        kind: 'chips',
        id: 'ql_smoking',
        label: 'Smoking',
        options: PN_SMOKING_OPTIONS,
        say: 'Do you smoke? And if you do, would you be willing to stop before the procedure?',
      },
    ],
  };

  const next: PacBeat = {
    id: 'next',
    title: track === 'self-pay' ? 'Payment and booking' : 'Next step',
    items: [
      onTrack('self-pay', track, SELF_PAY_NEXT_STEP),
      onTrack('employer', track, [
        {
          kind: 'group',
          when: (answers) => !(employerApplies(answers) && hasPartnerBenefit(answers)),
          items: [{ kind: 'note', text: 'The next step follows a partner employer benefit in Employer benefit.' }],
        },
        {
          kind: 'group',
          when: (answers) => employerApplies(answers) && hasPartnerBenefit(answers),
          items: [
            { kind: 'action', label: 'How approval works', say: EMPLOYER_APPROVAL },
            {
              kind: 'employerLocationSelect',
              id: PN_EMPLOYER_LOCATION_ID,
              label: 'Location',
              say: 'With your employer’s benefit, you can have the procedure at one of these locations. Which works best for you?',
            },
            {
              kind: 'chips',
              id: PN_BOOK_ONCE_APPROVED_ID,
              label: 'Book once approved?',
              options: PN_BOOK_ONCE_APPROVED_OPTIONS,
              say: BOOK_ONCE_APPROVED_SAY,
            },
            { kind: 'verificationTask', id: PN_EMPLOYER_TASK_ID, coverage: 'employer' },
          ],
        },
      ]),
      onTrack('insurance', track, [
        ...unavailable,
        whenAvailable([
          {
            kind: 'chips',
            id: PN_INSURANCE_TRAVEL_ID,
            label: 'Location',
            options: INSURANCE_TRAVEL_OPTIONS,
            say: 'Insurance cases are done in Charleston or Detroit. Which works better for you?',
          },
          {
            kind: 'group',
            when: (answers) => !meetsInsuranceCriteria(answers),
            items: [{ kind: 'note', text: 'The next step follows a likely eligible result in Insurance check.' }],
          },
          {
            kind: 'group',
            when: meetsInsuranceCriteria,
            items: [
              { kind: 'action', label: 'How approval works', say: INSURANCE_APPROVAL },
              {
                kind: 'chips',
                id: PN_INSURANCE_BOOK_ONCE_APPROVED_ID,
                label: 'Book once approved?',
                options: PN_BOOK_ONCE_APPROVED_OPTIONS,
                say: BOOK_ONCE_APPROVED_SAY,
              },
              { kind: 'verificationTask', id: PN_INSURANCE_TASK_ID, coverage: 'insurance' },
            ],
          },
        ]),
      ]),
    ],
  };

  const outcome: PacBeat = {
    id: 'outcome',
    title: 'Outcome',
    items: [
      onTrack('self-pay', track, [
        { kind: 'select', id: OUTCOME_ID, label: 'Outcome', options: PN_SELF_PAY_OUTCOME_OPTIONS },
      ]),
      onTrack('employer', track, [
        { kind: 'select', id: OUTCOME_ID, label: 'Outcome', options: PN_COVERAGE_OUTCOME_OPTIONS },
      ]),
      onTrack('insurance', track, [
        { kind: 'select', id: OUTCOME_ID, label: 'Outcome', options: PN_COVERAGE_OUTCOME_OPTIONS },
      ]),
      { kind: 'multiChips', id: 'barrier', label: 'Potential barriers', options: PN_BARRIER_OPTIONS },
      { kind: 'text', id: 'fun_facts', label: 'Fun facts' },
    ],
  };

  return [intro, check, procedure, medical, next, outcome];
}

/* -------------------------------------------------------------------------
   The glance row (BAR-2996)
   ------------------------------------------------------------------------- */

/** The canon status marks: ✓ settled, ● still open, ✗ closed off, none for neutral. */
export type PnGlanceMark = 'ok' | 'pending' | 'bad' | 'none';

export interface PnConsultationGlance {
  /** The outcome spelled out, or undefined when none is recorded. */
  outcome?: string;
  mark: PnGlanceMark;
  /** `Self-pay`, `Employer · Carrum`, `Insurance · Cigna`, or the bare path when its detail is not known. */
  track: string;
  /** The location picked in Next step, when there is one. */
  location?: string;
}

const OUTCOME_MARKS: Record<PnOutcome, PnGlanceMark> = {
  'Committed, likely payment in 72 hours': 'ok',
  'Committed, benefit verification next': 'ok',
  'Likely, still deciding': 'pending',
  'Lukewarm, will decide soon': 'pending',
  'Maybe in the future': 'none',
  'Not qualified': 'bad',
  'Not interested': 'bad',
};

function value(answers: PacBeatAnswers, id: string): string | undefined {
  const raw = answers.values[id];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

/**
 * The track a note shows when nothing picks one live: the chip it recorded,
 * else the track the card last read while it was worked (BAR-2925), else
 * self-pay.
 */
export function recordedCardTrack(answers: PacBeatAnswers): PnCardTrack {
  const chip = answers.values[PN_CARD_TRACK_ID];
  if (isPnCardTrack(chip)) return chip;
  return cardTrackForPatientTrack(value(answers, PN_CALL_TRACK_VALUE_ID) as Track | undefined);
}

/** `call_track`, named here rather than imported: `pn-consultation-note.ts` imports this module. */
const PN_CALL_TRACK_VALUE_ID = 'call_track';
/** The self-pay Next step's location select's answer id. */
const SELF_PAY_LOCATION_ID = 'loc';

/**
 * A saved card's glance row: Outcome · Track · Location (BAR-2996). Each column
 * reads the path the card shows, so another track's answers never leak into it.
 */
export function buildPnConsultationGlance(answers: PacBeatAnswers): PnConsultationGlance {
  const outcome = value(answers, OUTCOME_ID);
  const mark = outcome ? (OUTCOME_MARKS[outcome as PnOutcome] ?? 'none') : 'none';
  const track = recordedCardTrack(answers);

  if (track === 'employer') {
    const benefit = describeEmployerBenefit(answers);
    const recorded = value(answers, PN_CALL_TRACK_VALUE_ID);
    const administrator = benefit?.covered
      ? ADMINISTRATOR_LABELS[benefit.administrator]
      : recorded === Track.CARRUM || recorded === Track.TRANSCARENT
        ? TrackLabel[recorded]
        : undefined;
    return {
      outcome,
      mark,
      track: [PN_CARD_TRACK_LABELS.employer, administrator].filter(Boolean).join(' · '),
      location: value(answers, PN_EMPLOYER_LOCATION_ID),
    };
  }
  if (track === 'insurance') {
    return {
      outcome,
      mark,
      track: [PN_CARD_TRACK_LABELS.insurance, value(answers, PN_INSURER_ID)].filter(Boolean).join(' · '),
      location: insuranceTravelMarket(value(answers, PN_INSURANCE_TRAVEL_ID)),
    };
  }
  return { outcome, mark, track: PN_CARD_TRACK_LABELS['self-pay'], location: value(answers, SELF_PAY_LOCATION_ID) };
}

/**
 * What the card chose for Intake and billing (BAR-3000): the procedure, and the
 * Location the shown track's Next step picked, as a market name. Takes the
 * answers that apply, so another track's location never counts.
 */
export function cardIntakeSelection(
  answers: PacBeatAnswers,
  track: PnCardTrack
): { procedure?: string; market?: string } {
  const procedure = value(answers, 'procedure');
  const market =
    track === 'insurance'
      ? insuranceTravelMarket(value(answers, PN_INSURANCE_TRAVEL_ID))
      : track === 'employer'
        ? value(answers, PN_EMPLOYER_LOCATION_ID)
        : value(answers, SELF_PAY_LOCATION_ID);
  return { procedure, market };
}
